import { CalendarInput } from "@/components/ui/forms/calendarInput"
import { DropdownInput } from "@/components/ui/forms/dropdownInput"
import { InputContainer } from "@/components/ui/forms/inputContainer"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { displayedFullDateFormat } from "@/modules/utils/dateUtils"
import { word } from "@modules/core/utils/i18n"
import { ProfileLabelVisibility } from "@modules/profile/myProfile/components/profileLabelVisibility"
import { type ProfileFormTabProps } from "@modules/profile/myProfile/profileFormikType"
import { Units } from "@modules/utils/unitType"
import { getDisplayUnits } from "@modules/utils/unitUtils"
import dayjs from "dayjs"
import { useMemo } from "react"
import { Text } from "@/components/ui/baseUi/text"

export function ProfileIdentifyForm({ isFormFieldInValid, getFormErrorMessage, formik }: ProfileFormTabProps) {
  const unitOptions = useMemo(
    () =>
      Units.map((unit) => ({
        value: unit,
        label: getDisplayUnits(unit),
      })),
    [],
  )

  return (
    <div className="mt-6 flex grow flex-col justify-start gap-6 px-4">
      <Text fontStyle="TM" className="mb-4 self-center">
        {word("profile.form.identity.title")}
      </Text>

      {/* Firstname input */}
      <InputContainer>
        <ProfileLabelVisibility
          label={word("profile.form.firstname.label")}
          name="firstname"
          isInvalid={isFormFieldInValid("firstname")}
          isRequiredInput
        />
        <StandardInput
          name="firstname"
          setFieldValue={formik.setFieldValue}
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("firstname")}
        />
      </InputContainer>

      {/* Last name input */}
      <InputContainer>
        <ProfileLabelVisibility
          label={word("profile.form.lastname.label")}
          name="lastname"
          isInvalid={isFormFieldInValid("lastname")}
          isRequiredInput
        />
        <StandardInput
          name="lastname"
          setFieldValue={formik.setFieldValue}
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("lastname")}
        />
      </InputContainer>

      {/* Username input */}
      <InputContainer>
        <ProfileLabelVisibility
          label={word("profile.form.username.label")}
          name="username"
          isInvalid={isFormFieldInValid("username")}
          isRequiredInput
        />
        <StandardInput
          name="username"
          setFieldValue={formik.setFieldValue}
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("username")}
        />
      </InputContainer>

      {/* Birthdate input */}
      <InputContainer>
        <ProfileLabelVisibility
          label={word("auth.signup.form.dateOfBirth")}
          name="birthdate"
          isInvalid={isFormFieldInValid("birthdate")}
          displayVisibleToggle
          visibility={formik.getFieldProps("birthdateVisibility").value}
          isRequiredInput
          onToggleVisibility={() =>
            formik.setFieldValue("birthdateVisibility", !formik.getFieldProps("birthdateVisibility").value)
          }
        />

        <CalendarInput
          name="birthdate"
          setFieldValue={formik.setFieldValue}
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("birthdate")}
          placeholder={word("auth.signup.form.dateOfBirth")}
          calendarProps={{
            maxDate: dayjs().toDate(),
            minDate: dayjs().subtract(100, "year").toDate(),
            dateFormat: displayedFullDateFormat,
            captionLayout: "dropdown",
          }}
        />
      </InputContainer>

      {/* Unit input */}
      <InputContainer>
        <ProfileLabelVisibility
          label={word("profile.unit.options.title")}
          name="unit"
          isInvalid={isFormFieldInValid("unit")}
          isRequiredInput
        />
        <DropdownInput error={getFormErrorMessage("unit")} options={unitOptions}>
          <Select
            defaultValue={formik.values.unit}
            onValueChange={(value) => formik.setFieldValue("unit", value)}
            items={unitOptions}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {unitOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </DropdownInput>
      </InputContainer>
    </div>
  )
}
